import type { PureMetadata } from '../../../../../../Model/Sample/ISampleMetaData.js'
import type { CommonConfig, CSVColumnSelectorValidationState } from '../CSVCommonState.js'
import type { CSVColumnSelectorReturn, CSVTransform } from '../CSVColumnSelectorReturn.js'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { fromSuggestion } from '../../ParseCSVModal.js'
import { CheckIcon } from '@heroicons/react/24/outline/index.js'
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid/index.js'
import { CSVMetadataSharedSelect } from '../CSVMetadataSharedSelect.js'
import { readCell } from '../CSVClearSentinel.js'
import { fieldsClearedBy } from '../../../../../../Model/Sample/metadataClearing.js'

export type MetadataStringConfig = CommonConfig & {
  setter: (p: PureMetadata, value: string) => void
}

/**
 * Select a string-valued csv column
 */
export type CSVStringSelectorProps = {
  config: MetadataStringConfig
  onChange: (config: MetadataStringConfig, e: CSVColumnSelectorReturn) => void
  disabled?: boolean
  rawCSVData: Record<string, string>[]
  csvHeaders: string[]
  renderInstead: (instead: ReactNode | undefined) => void
}

export function CSVStringSelector(props: CSVStringSelectorProps) {
  const [validationState, setValidationState] = useState<CSVColumnSelectorValidationState>('NO_DATA')
  const [selectedOption, setSelectedOption] = useState<string>('')

  useEffect(() => {
    if (props.rawCSVData && props.config.type) {
      const suggestion = fromSuggestion(props.config.type, props.rawCSVData, props.csvHeaders)
      setSelectedOption(suggestion)

      if (suggestion) {
        const newValidationState = 'VALIDATED'
        setValidationState(newValidationState)
      }
    }
  }, [props.csvHeaders, props.rawCSVData, props.config.type])

  const reactSelectOptions = [
    {
      value: '',
      label: 'Select Column matching the criteria',
      rawKey: ''
    },
    ...props.csvHeaders.map((d: string) => ({
      value: d,
      label: d,
      rawKey: d
    }))
  ]

  const selectedValue = reactSelectOptions.find((opt) => opt.value === selectedOption) || null

  const handleTransform: CSVTransform = (args) => {
    const cell = readCell(args.row, selectedOption)
    if (cell.kind === 'clear') {
      args.clear(fieldsClearedBy(props.config.type))
    } else if (cell.kind === 'value') {
      props.config.setter(args.result, cell.value)
    }
  }

  const handleChange = (selected: (typeof reactSelectOptions)[number] | null) => {
    setSelectedOption(selected?.value ?? '')
  }

  useEffect(() => {
    const newValidationState = !selectedOption ? 'NO_DATA' : 'VALIDATED'
    setValidationState(newValidationState)
    props.onChange(props.config, {
      selectedColumn: selectedOption,
      validationState: newValidationState,
      transform: handleTransform
    })
  }, [selectedOption])

  return (
    <>
      <CSVMetadataSharedSelect
        config={props.config}
        disabled={props.disabled}
        selectedValue={selectedValue}
        options={reactSelectOptions}
        onChange={handleChange}
        validationState={validationState}
      />

      {/* Status Icon Button - only shown if a column is selected */}
      {validationState !== 'NO_DATA' && (
        <button
          className={`${
            validationState === 'VALIDATED' ? 'text-green-600 hover:text-green-800' : 'text-yellow-600 hover:text-yellow-800'
          } text-lg`}
          onClick={() => {
            if (validationState === 'INVALID_VALUES') {
              console.error('No handler for this - string values should not become invalid')
            }
          }}
          title={
            validationState === 'VALIDATED'
              ? 'All values are valid - click to view or edit mappings'
              : `Some entries cannot be resolved to allowed values.`
          }
        >
          {validationState === 'VALIDATED' ? <CheckIcon className="h-5 w-5" /> : <ExclamationTriangleIcon className="h-5 w-5" />}
        </button>
      )}
      {validationState === 'NO_DATA' && <span></span>}
    </>
  )
}
